<template>
  <!--
    As alterações em lote, no topo da tela de ajustes.

    É o que sobrou da tela `/lotes-financeiros`: a pergunta "o que mexeu no saldo dos PNs?"
    continua existindo, mas não precisava de rota, menu, filtros e export para ser respondida.

    Três estados, e o terceiro é o que faltava:

      A) há não lidos    → o alerta de sempre
      B) nada pendente   → um link discreto "ver alterações recentes"
      C) painel aberto   → alternador Não lidos | Todos

    ⚠️ O estado B existe por causa do critério de aceite: "o Financeiro deve receber OU conseguir
    consultar o resumo". Antes, marcar o último lote como lido fazia o painel inteiro desaparecer
    debaixo do cursor, no meio da leitura — o ato de confirmar que leu apagava o que estava sendo
    lido, e depois não havia onde procurar.
  -->
  <div v-if="canReadBatches">
    <financial-batch-acknowledge-modal />

    <b-alert
      v-if="unreadOperations.length"
      variant="warning"
      show
      class="mb-1"
      data-testid="unread-financial-batches-strip"
    >
      <div class="alert-body d-flex align-items-center flex-wrap">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-1" />
        <span class="mr-1">
          <strong>
            {{ unreadOperations.length }}
            {{ unreadOperations.length === 1 ? "alteração em lote" : "alterações em lote" }}
          </strong>
          {{ resumo }}
        </span>
        <b-link
          class="ml-auto font-weight-bold"
          data-testid="unread-financial-batches-toggle"
          @click="abrir('unread')"
        >
          {{ expanded && scope === 'unread' ? "Ocultar" : "Conferir" }}
        </b-link>
      </div>
    </b-alert>

    <!--
      Sem caixa, sem cor de alerta: nada está pendente, e um aviso amarelo permanente treinaria
      o olho a ignorar a faixa inteira — que é justamente como o caso Kauê passou despercebido.
    -->
    <div v-else class="text-right mb-1">
      <b-link
        class="text-muted"
        data-testid="recent-financial-batches-toggle"
        @click="abrir('all')"
      >
        <feather-icon icon="ClockIcon" size="14" />
        <small>{{ expanded ? "Ocultar alterações recentes" : "Ver alterações recentes" }}</small>
      </b-link>
    </div>

    <b-card v-if="expanded" no-body class="mb-1">
      <b-card-body>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <b-form-radio-group
            v-model="scope"
            buttons
            size="sm"
            button-variant="outline-secondary"
            :options="scopeOptions"
            data-testid="financial-batches-scope"
            @change="onScopeChange"
          />
          <b-spinner v-if="loading" small />
        </div>

        <div
          v-if="!operations.length"
          class="text-muted text-center py-1"
          data-testid="financial-batches-empty"
        >
          <small>{{ mensagemVazia }}</small>
        </div>

        <div
          v-for="operation in operations"
          :key="operation.reference"
          class="border rounded p-1 mb-1"
          :data-testid="`financial-batch-${operation.reference}`"
        >
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div>
              <div class="text-primary font-weight-bold">
                {{ operation.reference }}
              </div>
              <div class="text-body">
                {{ operation.title }}
              </div>
            </div>
            <b-badge :variant="originVariant(operation)">
              {{ originLabel(operation) }}
            </b-badge>
          </div>

          <!--
            Os SETE campos que o §3.3 do card exige no resumo. `affected_contracts_count` e
            `competences` faltavam: eram gravados pelo runner desde o primeiro dia e nunca
            chegavam à tela. O e-mail já os mostrava — a tela, não.
          -->
          <b-row>
            <b-col cols="4" class="mb-1">
              <small class="text-muted d-block">PNs afetados</small>
              {{ operation.affected_consultants_count || 0 }}
            </b-col>
            <b-col cols="4" class="mb-1">
              <small class="text-muted d-block">Contratos alterados</small>
              {{ operation.affected_contracts_count || 0 }}
            </b-col>
            <b-col cols="4" class="mb-1">
              <small class="text-muted d-block">Efeito líquido</small>
              {{ operation.net_diff | toCurrency }}
            </b-col>
            <b-col cols="4" class="mb-1">
              <small class="text-muted d-block">Competências</small>
              {{ competenciasLabel(operation) }}
            </b-col>
            <b-col cols="8" class="mb-1">
              <small class="text-muted d-block">Quem executou</small>
              {{ executorLabel(operation) }}
            </b-col>
            <b-col v-if="operation.reason" cols="12" class="mb-1">
              <small class="text-muted d-block">Motivo</small>
              {{ operation.reason }}
            </b-col>
          </b-row>

          <!--
            Operação DETECTADA é a que ninguém abriu: o observer viu escrita em massa fora de um
            contexto declarado e criou a cabeça sozinho. Dizer isso importa mais que o resto do
            card — é o caso Kauê sendo pego na hora em vez de dois meses depois.
          -->
          <div v-if="isDetected(operation)" class="bg-light rounded p-1 mb-1">
            <small>
              Escrita em massa fora de uma operação declarada. O sistema registrou sozinho,
              com a origem no código.
            </small>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <!--
              `status: []` é obrigatório: sem ele um default de status na lista poderia esconder
              justamente os lançamentos já aprovados deste lote.
            -->
            <router-link
              :to="{
                name: 'financial-adjustments-list',
                query: { batch_reference: operation.reference, status: [] }
              }"
              target="_blank"
            >
              <small>
                Ver os lançamentos
                <feather-icon icon="ExternalLinkIcon" size="12" />
              </small>
            </router-link>

            <!--
              ⚠️ `canReview` vem do backend (`tokenCan`), não de um palpite da tela. `read` vai
              para doze papéis e `review` para sete: oferecer o botão a quem não pode custa 403,
              e o interceptor de helpers/api.js DESLOGA em 403 — o clique custaria a sessão.
            -->
            <b-button
              v-if="canReview && isPending(operation)"
              variant="primary"
              size="sm"
              :data-testid="`financial-batch-read-${operation.reference}`"
              @click="openRead(operation)"
            >
              Marcar como lido
            </b-button>
            <span v-else-if="isAcknowledged(operation)" class="text-muted">
              <feather-icon icon="CheckIcon" size="14" class="text-success" />
              <small>{{ leituraLabel(operation) }}</small>
            </span>
            <b-badge v-else-if="isQuestioned(operation)" variant="light-warning">
              Questionado
            </b-badge>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCol, BFormRadioGroup, BLink, BRow, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { FINANCIAL_BATCH, READ_ACTION } from '@/constants/resources'
import * as types from '../store/types'
import FinancialBatchAcknowledgeModal from './FinancialBatchAcknowledgeModal'
import {
  ACKNOWLEDGED_REVIEW_STATUS,
  DETECTED_ORIGIN,
  PENDING_REVIEW_STATUS,
  QUESTIONED_REVIEW_STATUS,
  batchOriginLabel,
  batchOriginVariant,
} from './financialBatchLabels'

const SCOPE_UNREAD = 'unread'
const SCOPE_ALL = 'all'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormRadioGroup,
    BLink,
    BRow,
    BSpinner,
    FinancialBatchAcknowledgeModal,
  },
  data() {
    return {
      expanded: false,
      scope: SCOPE_UNREAD,
      loading: false,
      scopeOptions: [
        { text: 'Não lidos', value: SCOPE_UNREAD },
        { text: 'Todos', value: SCOPE_ALL },
      ],
    }
  },
  computed: {
    ...mapGetters({
      unread: types.UNREAD_FINANCIAL_BATCHES,
      recent: types.RECENT_FINANCIAL_BATCHES,
    }),
    /**
     * ⚠️ Sem esta guarda, TODO usuário que abre a tela de ajustes dispara uma chamada a uma rota
     * que exige `financial_batch:read`. Abilities são cunhadas no token no LOGIN: no dia do
     * deploy, quem já está logado não a tem, toma 403, e o interceptor de helpers/api.js
     * DESLOGA em 403. O `.catch` não protege — o interceptor não rejeita, ele desloga.
     */
    canReadBatches() {
      return this.$can(READ_ACTION, FINANCIAL_BATCH)
    },
    unreadOperations() {
      return (this.unread && this.unread.operations) || []
    },
    operations() {
      const fonte = this.scope === SCOPE_ALL ? this.recent : this.unread
      return (fonte && fonte.operations) || []
    },
    canReview() {
      return Boolean(this.unread && this.unread.can_review)
    },
    mensagemVazia() {
      return this.scope === SCOPE_ALL
        ? 'Nenhuma alteração em lote registrada.'
        : 'Nada pendente de leitura.'
    },
    /**
     * A frase depois do número, montada inteira aqui.
     *
     * ⚠️ O verbo concorda com a quantidade de OPERAÇÕES, não de PNs — com os dois pedaços
     * separados saía "1 alteração em lote mexeram no saldo".
     *
     * ⚠️ Conta sempre os NÃO LIDOS, mesmo quando o painel exibe "Todos". É por isso que o store
     * tem dois slots: com um só, esta frase passaria a afirmar que lotes já revisados não foram
     * lidos.
     *
     * A soma de PNs não é distinta entre operações: o mesmo PN tocado por dois lotes conta duas
     * vezes. Por isso a frase nunca promete "N PNs afetados" como se fosse um total limpo.
     */
    resumo() {
      const uma = this.unreadOperations.length === 1
      const verbo = uma ? 'mexeu' : 'mexeram'
      const pns = this.unreadOperations.reduce(
        (soma, operacao) => soma + (Number(operacao.affected_consultants_count) || 0),
        0,
      )
      const alvo = pns
        ? `no saldo de ${pns} ${pns === 1 ? 'PN' : 'PNs'}`
        : 'no saldo de PNs'
      return `${verbo} ${alvo} e você ainda não leu.`
    },
  },
  mounted() {
    if (!this.canReadBatches) return

    // Falha em silêncio de propósito: esta faixa é um aviso, não a função da tela. Um toast de
    // erro aqui atrapalharia quem veio aprovar ajuste e não tem nada a ver com lote.
    this.getUnreadBatches().catch(() => {})
  },
  methods: {
    ...mapActions({
      getUnreadBatches: types.GET_UNREAD_FINANCIAL_BATCHES,
      getRecentBatches: types.GET_RECENT_FINANCIAL_BATCHES,
      openAcknowledgeModal: types.OPEN_FINANCIAL_BATCH_ACKNOWLEDGE_MODAL,
    }),
    isPending(operation) {
      return operation.review_status === PENDING_REVIEW_STATUS
    },
    isAcknowledged(operation) {
      return operation.review_status === ACKNOWLEDGED_REVIEW_STATUS
    },
    isQuestioned(operation) {
      return operation.review_status === QUESTIONED_REVIEW_STATUS
    },
    isDetected(operation) {
      return operation.origin === DETECTED_ORIGIN
    },
    originLabel(operation) {
      return batchOriginLabel(operation.origin)
    },
    originVariant(operation) {
      return batchOriginVariant(operation.origin)
    },
    executorLabel(operation) {
      const partes = [operation.executed_by_name, operation.executed_by_role_name].filter(Boolean)
      // Operação detectada não tem autor declarado — foi o observer que a criou sozinho, e dizer
      // isso é mais útil do que deixar o campo vazio.
      return partes.length ? partes.join(' · ') : 'não declarado'
    },
    competenciasLabel(operation) {
      const competencias = Array.isArray(operation.competences) ? operation.competences : []
      if (!competencias.length) return '—'
      // Até três, depois "+N": a coluna é estreita e a lista completa vira ruído.
      if (competencias.length <= 3) return competencias.join(', ')
      return `${competencias.slice(0, 3).join(', ')} +${competencias.length - 3}`
    },
    leituraLabel(operation) {
      return operation.reviewed_by_name ? `Lido por ${operation.reviewed_by_name}` : 'Lido'
    },
    abrir(scope) {
      if (this.expanded && this.scope === scope) {
        this.expanded = false
        return
      }
      this.expanded = true
      this.scope = scope
      this.onScopeChange(scope)
    },
    /**
     * Busca o histórico UMA vez e o mantém. Alternar de volta e voltar não custa requisição — e
     * o dia normal, com a fila vazia, tem o mesmo custo de rede de antes.
     */
    onScopeChange(scope) {
      if (scope !== SCOPE_ALL || (this.recent && this.recent.loaded)) return

      this.loading = true
      this.getRecentBatches()
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    openRead(operation) {
      this.openAcknowledgeModal({
        reference: operation.reference,
        // Relê as DUAS listas: a operação sai da fila e entra no histórico como lida.
        saveAction: () => {
          this.getUnreadBatches().catch(() => {})
          this.getRecentBatches().catch(() => {})
        },
      })
    },
  },
}
</script>
